import { action, computed, observable } from 'mobx';

import { conceptoKeyNamePairs } from './constants';

import {
    AguaUsage,
    AguaUsoRecaudacion,
    AguaUsoUnidades,
    ImpuestosAguaCuentas,
    ImpuestosAguaCuentasRecord,
    ImpuestosAguaIngresosPorUso,
    ImpuestosAguaIngresosPorUsoRecord,
    ImpuestosAguaPagosRecord,
    RegistroImpuestosEstado
} from '../models';
import { setValueByPath } from './utils';

import { registroImpuestosAguaStore as aguaStore } from './registroImpuestosAguaStore';

export class ImpuestosAguaMedidorStore {
    kind: 'ingresosPorUso' | 'cuentas';

    @observable
    isSaving = false;

    constructor(kind: 'ingresosPorUso' | 'cuentas') {
        this.kind = kind;
    }

    @computed
    get isLoading() {
        return aguaStore.isLoading;
    }

    @computed
    get ejercicio() {
        return aguaStore.apertura.ejercicio;
    }

    @computed
    get registroImpuestos() {
        return aguaStore.registroImpuestos;
    }

    @computed
    get IngresosPorUsoConMediadorRecords(): ImpuestosAguaIngresosPorUsoRecord[] {
        return this.toMedidorIngresosPorUsoRecords('ConMedidor');
    }

    @computed
    get IngresosPorUsoSinMediadorRecords(): ImpuestosAguaIngresosPorUsoRecord[] {
        return this.toMedidorIngresosPorUsoRecords('SinMedidor');
    }

    @computed
    get IngresosPorUsoTotalRecords(): ImpuestosAguaIngresosPorUsoRecord[] {
        const conMedidorRecords = this.IngresosPorUsoConMediadorRecords;
        const sinMedidorRecords = this.IngresosPorUsoSinMediadorRecords;
        const conMedidorTotal = conMedidorRecords[conMedidorRecords.length - 1];
        const sinMedidorTotal = sinMedidorRecords[sinMedidorRecords.length - 1];

        return [
            {
                key: 'TOTAL',
                concepto: 'TOTAL',
                usuario: conMedidorTotal.usuario + sinMedidorTotal.usuario,
                toma: conMedidorTotal.toma + sinMedidorTotal.toma,
                importe: conMedidorTotal.importe + sinMedidorTotal.importe
            }
        ];
    }

    @action
    setIngresosPorUsoValue = (path: string, value: number) => {
        const { ingresosPorUso } = this.registroImpuestos;
        setValueByPath(ingresosPorUso, path, value);

        if (path.includes('usuario')) {
            setValueByPath(ingresosPorUso, path.replace('usuario', 'toma'), value);
        }

        aguaStore.markDirty();
    };

    @action
    saveMedidor = async () => {
        this.isSaving = true;

        const { registroImpuestos } = aguaStore;
        const { estado, id } = registroImpuestos;
        const subform = registroImpuestos[this.kind];
        const formatoNum = this.kind === 'ingresosPorUso' ? 2 : 3;

        if (estado === RegistroImpuestosEstado.SinCaptura || id === 0) {
            await aguaStore.getNewForm();
        }

        const result = await aguaStore.saveForm(subform, formatoNum);
        aguaStore.unmarkDirty();

        this.isSaving = false;

        if (result === 'success') {
            aguaStore.registroImpuestos[this.kind].isSaved = true;
        }
    };

    @computed
    get IngresosPorUsoPagos(): ImpuestosAguaPagosRecord[] {
        const { ingresosPorUso } = this.registroImpuestos;

        const {
            numeroDePagosEjercicio,
            numeroDePagosEjercicioAnterior,
            numeroDePagosEjercicioMixto
        } = ingresosPorUso;

        return [
            Object.entries({
                numeroDePagosEjercicio,
                numeroDePagosEjercicioAnterior,
                numeroDePagosEjercicioMixto
            }).reduce(
                (acc, [key, aguaCount]) => {
                    const { usuario } = aguaCount;

                    acc[`${key}.usuario`] = usuario;
                    acc[`${key}.toma`] = usuario;
                    acc[`pagosTotal.usuario`] += usuario;
                    acc[`pagosTotal.toma`] += usuario;

                    return acc;
                },
                {
                    key: 'ingresosPorUso',
                    'pagosTotal.usuario': 0,
                    'pagosTotal.toma': 0
                } as any
            )
        ];
    }

    @computed
    get CuentasPagos(): ImpuestosAguaPagosRecord[] {
        const { cuentas } = this.registroImpuestos;

        const {
            numeroCuentasPagosEjercicio,
            numeroCuentasPagosEjercicioAnterior,
            numeroCuentasPagosEjercicioMixto
        } = cuentas;

        return [
            Object.entries({
                numeroCuentasPagosEjercicio,
                numeroCuentasPagosEjercicioAnterior,
                numeroCuentasPagosEjercicioMixto
            }).reduce(
                (acc, [key, aguaCount]) => {
                    const { usuario } = aguaCount;

                    acc[`${key}.usuario`] = usuario;
                    acc[`${key}.toma`] = usuario;
                    acc[`pagosTotal.usuario`] += usuario;
                    acc[`pagosTotal.toma`] += usuario;

                    return acc;
                },
                {
                    key: 'cuentas',
                    'pagosTotal.usuario': 0,
                    'pagosTotal.toma': 0
                } as any
            )
        ];
    }

    @action
    setCuentasValue = (path: string, value: number) => {
        const { cuentas } = this.registroImpuestos;
        setValueByPath(cuentas, path, value);

        if (path.includes('usuario')) {
            setValueByPath(cuentas, path.replace('usuario', 'toma'), value);
        }

        aguaStore.markDirty();
    };

    @computed
    get cuentasConMediadorRecords(): ImpuestosAguaCuentasRecord[] {
        return this.getMedidorCuentasTableRows('ConMedidor');
    }

    @computed
    get cuentasSinMediadorRecords(): ImpuestosAguaCuentasRecord[] {
        return this.getMedidorCuentasTableRows('SinMedidor');
    }

    @computed
    get cuentasTotalRecords(): ImpuestosAguaCuentasRecord[] {
        const conMedidorRecords = this.cuentasConMediadorRecords;
        const sinMedidorRecords = this.cuentasSinMediadorRecords;
        const conMedidorTotal = conMedidorRecords[conMedidorRecords.length - 1];
        const sinMedidorTotal = sinMedidorRecords[sinMedidorRecords.length - 1];
        const total = 'TOTAL';

        return [
            {
                key: 'TOTAL',
                concepto: 'TOTAL',
                [`numeroDeCuentasEjercicio${total}.usuario`]:
                    conMedidorTotal[
                        'numeroDeCuentasEjercicioConMedidor.usuario' as keyof ImpuestosAguaCuentasRecord
                    ] +
                    sinMedidorTotal[
                        `numeroDeCuentasEjercicioSinMedidor.usuario` as keyof ImpuestosAguaCuentasRecord
                    ],
                [`numeroDeCuentasEjercicio${total}.toma`]:
                    conMedidorTotal[
                        'numeroDeCuentasEjercicioConMedidor.toma' as keyof ImpuestosAguaCuentasRecord
                    ] +
                    sinMedidorTotal[
                        `numeroDeCuentasEjercicioSinMedidor.toma` as keyof ImpuestosAguaCuentasRecord
                    ],
                [`numeroDeCuentasEjercicioAnterior${total}.usuario`]:
                    conMedidorTotal[
                        'numeroDeCuentasEjercicioAnteriorConMedidor.usuario' as keyof ImpuestosAguaCuentasRecord
                    ] +
                    sinMedidorTotal[
                        `numeroDeCuentasEjercicioAnteriorSinMedidor.usuario` as keyof ImpuestosAguaCuentasRecord
                    ],
                [`numeroDeCuentasEjercicioAnterior${total}.toma`]:
                    conMedidorTotal[
                        'numeroDeCuentasEjercicioAnteriorConMedidor.toma' as keyof ImpuestosAguaCuentasRecord
                    ] +
                    sinMedidorTotal[
                        `numeroDeCuentasEjercicioAnteriorSinMedidor.toma` as keyof ImpuestosAguaCuentasRecord
                    ]
            }
        ];
    }

    private toMedidorIngresosPorUsoRecords(type: 'ConMedidor' | 'SinMedidor') {
        const { ingresosPorUso } = this.registroImpuestos;
        const ingresosPorTiposUso = ingresosPorUso[
            `ingresosPorTiposUso${type}` as keyof ImpuestosAguaIngresosPorUso
        ] as AguaUsage<AguaUsoRecaudacion>;

        let usuarioSum = 0;
        let importeSum = 0;

        const tableRecords = Array.from(conceptoKeyNamePairs.keys()).map((concepto, index) => {
            const { usuario: usuario, importe: importe } = ingresosPorTiposUso[concepto];

            usuarioSum += usuario;
            importeSum += importe;

            return {
                key: concepto,
                concepto: conceptoKeyNamePairs.get(concepto) as string,
                usuario,
                toma: usuario,
                importe
            };
        });

        // Total Row
        (tableRecords as any[]).push({
            key: `${type}total`,
            concepto:
                'TOTAL USO DE LA BASE ' + (type === 'ConMedidor' ? 'CON MEDIDOR' : 'SIN MEDIDOR'),
            usuario: usuarioSum,
            toma: usuarioSum,
            importe: importeSum
        });

        return tableRecords;
    }

    private getMedidorCuentasTableRows(
        type: 'ConMedidor' | 'SinMedidor'
    ): ImpuestosAguaCuentasRecord[] {
        const { cuentas } = this.registroImpuestos;
        const numeroDeCuentasEjercicio = cuentas[
            `numeroDeCuentasEjercicio${type}` as keyof ImpuestosAguaCuentas
        ] as AguaUsage<AguaUsoUnidades>;
        const numeroDeCuentasEjercicioAnterior = cuentas[
            `numeroDeCuentasEjercicioAnterior${type}` as keyof ImpuestosAguaCuentas
        ] as AguaUsage<AguaUsoUnidades>;

        let usuarioASum = 0;
        let usuarioBSum = 0;

        const tableRecords = Array.from(conceptoKeyNamePairs.keys()).map(concepto => {
            const { usuario: usuarioA } = numeroDeCuentasEjercicio[concepto];
            const { usuario: usuarioB } = numeroDeCuentasEjercicioAnterior[concepto];

            usuarioASum += usuarioA;
            usuarioBSum += usuarioB;

            return {
                key: `${concepto}`,
                concepto: conceptoKeyNamePairs.get(concepto) as string,
                [`numeroDeCuentasEjercicio${type}.usuario`]: usuarioA,
                [`numeroDeCuentasEjercicio${type}.toma`]: usuarioA,
                [`numeroDeCuentasEjercicioAnterior${type}.usuario`]: usuarioB,
                [`numeroDeCuentasEjercicioAnterior${type}.toma`]: usuarioB
            };
        });

        // Total Row
        tableRecords.push({
            key: `${type}total`,
            concepto:
                'TOTAL USO DE LA BASE ' + (type === 'ConMedidor' ? 'CON MEDIDOR' : 'SIN MEDIDOR'),
            [`numeroDeCuentasEjercicio${type}.usuario`]: usuarioASum,
            [`numeroDeCuentasEjercicio${type}.toma`]: usuarioASum,
            [`numeroDeCuentasEjercicioAnterior${type}.usuario`]: usuarioBSum,
            [`numeroDeCuentasEjercicioAnterior${type}.toma`]: usuarioBSum
        });

        return tableRecords;
    }
}

export const impuestosAguaCuentasStore = new ImpuestosAguaMedidorStore('cuentas');
export const impuestosAguaPorUsoStore = new ImpuestosAguaMedidorStore('ingresosPorUso');
